import React, { useMemo } from 'react';

import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  NavBarItem,
  BottomNavBar,
  IconRenderer,
} from '../../shared/BottomNavBar';
import {
  BasicUserIcon,
  BasicHomeIcon,
  BasicSearchIcon,
  BasicMenuIcon,
  TimeCalendarIcon,
} from '../../ui/Icons';
import { CloseableNotificationBubble } from './NotificationBubble';

export type SelectionOption = 'home' | 'search' | 'appts' | 'account' | 'log-in' | 'more';

type ActionHandler = () => void;
type ClientNavBarProps = {
  onHomePress: ActionHandler;
  onSearchPress: ActionHandler;
  onAppointmentsPress: ActionHandler;
  onAccountPress: ActionHandler;
  onMorePress: ActionHandler;
  selectedItem?: SelectionOption;
  showHomeUnreadBadge?: boolean;
  onCloseHomeBubbleTooltip?: ActionHandler;
  homeBubbleTooltipProps?: React.ComponentProps<typeof CloseableNotificationBubble>;
};

function useMemoizedProps<T extends { [key: string]: any }>(props: T): T {
  return (
    useMemo<T>(
      () => ({ ...props } as T), [
        ...Object.keys(props),
        ...Object.values(props),
      ],
    )
  );
}

export const ClientNavBar: React.FC<ClientNavBarProps> = ({
  onHomePress,
  onSearchPress,
  onAppointmentsPress,
  onAccountPress,
  onMorePress,
  selectedItem,
  showHomeUnreadBadge,
  onCloseHomeBubbleTooltip,
  homeBubbleTooltipProps,
}) => {
  const renderIcon: IconRenderer = ({ selected }, Icon) => (
    <Box
      style={{ height: 20, width: 20 }}
      justifyContent="center"
      alignItems="center"
    >
      <Icon color={selected ? colors.punch : colors.ash} />
    </Box>
  );

  const closeHomeBubbleTooltipProps = (
    useMemoizedProps({ onClick: onCloseHomeBubbleTooltip })
  );

  return (
    <BottomNavBar>
      <NavBarItem
        id="home"
        label="Home"
        icon={BasicHomeIcon}
        onSelect={onHomePress}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
        selected={selectedItem === 'home'}
        showBadge={showHomeUnreadBadge}
        badgeAppearance="client"
      >
        {homeBubbleTooltipProps && (
          <CloseableNotificationBubble
            testID="home-nav-item-bubble-tooltip"
            textTouchableProps={closeHomeBubbleTooltipProps}
            closeIconTouchableProps={closeHomeBubbleTooltipProps}
            {...homeBubbleTooltipProps}
          />
        )}
      </NavBarItem>
      <NavBarItem
        id="search"
        label="Search"
        icon={BasicSearchIcon}
        onSelect={onSearchPress}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
        showBadgeWhenSelected
        showBadge={false}
        badgeAppearance="client"
        selected={selectedItem === 'search'}
      />
      <NavBarItem
        id="appts"
        label="Appts"
        icon={TimeCalendarIcon}
        onSelect={onAppointmentsPress}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
        selected={selectedItem === 'appts'}
      />
      <NavBarItem
        id="account"
        label="Account"
        icon={BasicUserIcon}
        onSelect={onAccountPress}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
        selected={selectedItem === 'account'}
      />
      <NavBarItem
        id="more"
        label="More"
        icon={BasicMenuIcon}
        onSelect={onMorePress}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
        selected={selectedItem === 'more'}
      />
    </BottomNavBar>
  );
};
